/**
 * 音声認識と会話制御を行うコンポーネント
 * ユーザー発話の認識とAIアシスタントの応答を制御する
 */
import React, { useEffect, useRef, useState } from "react";
import { EventEmitter } from "events";
import { useAtom } from "jotai/index";
import SpeechRecognition, { useSpeechRecognition } from "react-speech-recognition";
import { createTRPCProxyClient, createWSClient, wsLink } from "@trpc/client";
import { useTranslation } from "react-i18next";

// ルートパスからのインポート
import type { AppRouter } from "~/../../server/src/api/root";
import type { Message, Character } from "shared/Schemas";
import { currentLocale, talkModes, responseTags } from "~/Commons";
import { getCurrentCustomerId } from "~/AtomWithDynamoDB";
import { api } from "~/api";
import { FILLER_SEND_THRESHOLD_MS, shouldSendFiller } from "~/utils/fillerUtils";
import "~/css/ChatPage.css";
import "~/css/MainContents.css";
import "~/css/VoiceSettings.css";
import { PlaySound, playVoice, PlayVoiceParams, audioManager } from "~/SoundUtils";
import {
  currentSpeakerAtom,
  currentVRMAtom,
  isFaceAtom,
  isGeneratingAtom,
  isInConversationCycleAtom,
  isListeningAtom,
  isOutputTextAtom,
  isProcessingAtom,
  isSessionAtom,
  isThinkingAtom,
  languageAtom,
  messagesAtom,
  outputTextAtom,
  queueArrayAtom,
  summaryQueueArrayAtom,
  currentAssistantMessageAtom,
  fullAssistantMessageAtom,
  standbyModeAtom,
  T2SEngineAtom,
  voiceIntonationAtom,
  voicePitchAtom,
  voiceSpeedAtom,
  volumeAtom,
  isSettingOpenAtom,
  isTranscribingAtom,
  isBubbleVisibleAtom,
  layoutSelectAtom,
  isConversationInterruptedAtom,
  isFaceDetectionAtom,
  difyWarmupCompletedAtom,
  isProgressModalReadyAtom,
  vrmPathAtom,
} from "~/Atom";
import {
  listeningModeAtom,
  waitTimerAtom,
  talkingModeAtom,
  isFirstResponseAtom,
  isCallOutAtom,
  styleModeAtom,
} from "~/AtomWithDynamoDB";
import { emitter } from "~/TTS/voicevox/VoiceSynthesis";
import { getVRMName } from "~/vrm_utils/VRMUtils";
import { showSessionPopup, triggerClosePopup } from "~/components/modal/showSessionPopup";
import { Tooltip } from "react-tooltip";
import ControlButtonLayout1 from "~/components/layout/ControlButtonLayout1";
import ControlButtonLayout2 from "~/components/layout/ControlButtonLayout2";
import ControlButtonLayout3 from "~/components/layout/ControlButtonLayout3";
import { useCustomVariables } from "~/components/settings/HamburgerMenu/hooks/useCustomVariables";
import { useNetworkMonitor } from "~/components/hooks/useNetworkMonitor";
import NetworkErrorModal from "~/components/modal/NetworkErrorModal";
import { warmupDifyConnection } from "~/utils/DifyWarmup";
import { normalizeCharacterName } from "~/utils/textNormalizationUtils";
import { v4 as uuidv4 } from "uuid";
import { selectByName } from "~/components/db/CharacterDBUtils";

const wsLocalUrl = process.env.REACT_APP_WEB_SOCKET_LOCAL_URL as string;
const wsCloudfrontUrl = process.env.REACT_APP_WEB_SOCKET_CLOUDFRONT_URL as string;

// 会話中断用EventEmitter
export const conversationEmitter = new EventEmitter();

// セッションIDを生成（コンポーネントが初回レンダリングされた時に1度だけ生成）
const sessionId = (() => {
  const stored = sessionStorage.getItem("aiConciergeSessionId");
  if (stored) {
    return stored;
  }
  const newId = uuidv4();
  sessionStorage.setItem("aiConciergeSessionId", newId);
  return newId;
})();

const getWsUrl = () => {
  const currentUrl = window.location.href;
  const baseUrl = currentUrl.startsWith("http://localhost:3000") ? wsLocalUrl : wsCloudfrontUrl;

  // セッションIDをクエリパラメータとして追加
  const url = new URL(baseUrl);
  url.searchParams.set("sessionId", sessionId);
  return url.toString();
};

// キャラクターの名前定義
const characterNames = {
  roboo: "ロボオ", // ロボオのキャラクター名
  roboco: "ロボコ", // ロボコのキャラクター名
  reico: "reico", // レイコのキャラクター名
};

// キャンセル時の音量調整倍率
const volumeMultiplier = 0.5;

// メッセージ関連の定数
const messageConstants = {
  defaultMessageCount: 3, // 初期メッセージの数
  minLength: 0, // 空文字列の長さ
};

// 疑似プレースホルダ表示制御
const getPseudoPlaceholderDisplay = (
  standbyMode: number,
  listeningMode: number,
  isPressed: boolean,
  isThinkingPlaceholder: boolean // t("thinking")を表示しているかどうか
): "none" | "block" =>
  !isPressed && listeningMode === 2 && standbyMode === talkModes.active && !isThinkingPlaceholder
    ? "block"
    : "none";

/**
 * 文字列の置換マップ
 * ユーザーごとに別途リストを用意予定
 */
const conversionMap: { [key: string]: string } = {
  路傍: "ロボオ",
  ロボを: "ロボオ",
  ロバー: "ロボオ",
  ローバー: "ロボオ",
  ロボ子: "ロボコ",
  れいこ: "レイコ",
  玲子: "レイコ",
  セックス: "シックス",
  瓶: "びん",
  塩釜市: "塩竈市",
  アイフェイス: "I-FACE",
  イーシーツー: "EC2",
};

/**
 * 会話タイムアウトの設定
 */
const talkTimeout = 60000; // 60秒
const timeoutExpression = "[sad]";

/**
 * 挨拶判定の結果
 */
const GreetingStatus = {
  yes: "YES", // 挨拶のみ
  no: "NO", // 挨拶以外のメッセージが含まれる
  exclusions: [
    // 除外ワードリスト
    "デバッグモード",
  ],
};

/**
 * 音声認識と会話制御を行うコントロールボタンコンポーネント
 */
// eslint-disable-next-line complexity
const ControlButton: React.FC = () => {
  // トランスレーション
  const { t, i18n } = useTranslation();

  // 状態管理
  const [placeholderText, setPlaceholderText] = useState(t(""));
  const [isProcessing, setIsProcessing] = useAtom(isProcessingAtom);
  const [isThinking, setIsThinking] = useAtom(isThinkingAtom);
  const [isListening, setIsListening] = useAtom(isListeningAtom);
  const { transcript, listening } = useSpeechRecognition();
  const [displayText, setDisplayText] = useState("");
  const [messages, setMessages] = useAtom(messagesAtom);
  const [standbyMode, setStandbyMode] = useAtom(standbyModeAtom);
  const [queueArray, setQueueArray] = useAtom(queueArrayAtom);
  const [summaryQueueArray, setSummaryQueueArray] = useAtom(summaryQueueArrayAtom);
  const [, setCurrentAssistantMessage] = useAtom(currentAssistantMessageAtom);
  const [fullAssistantMessage, setFullAssistantMessage] = useAtom(fullAssistantMessageAtom);
  const [T2SEngine] = useAtom(T2SEngineAtom);
  const [currentVRM] = useAtom(currentVRMAtom);
  const [currentSpeaker] = useAtom(currentSpeakerAtom);
  const [, setOutputText] = useAtom(outputTextAtom);
  const [isOutputText, setIsOutputText] = useAtom(isOutputTextAtom);
  const [speed] = useAtom(voiceSpeedAtom);
  const [pitch] = useAtom(voicePitchAtom);
  const [intonation] = useAtom(voiceIntonationAtom);
  const [volume] = useAtom(volumeAtom);
  const waitTurn = useRef(0); // セッション終了までの現在の待ち時間
  const [waitTimer] = useAtom(waitTimerAtom); // セッション終了までの最大待ち時間
  const [isSession, setIsSession] = useAtom(isSessionAtom);
  const initSessionFlag = useRef(false);
  const [, setIsGenerating] = useAtom(isGeneratingAtom);
  const [isInConversationCycle, setIsInConversationCycle] = useAtom(isInConversationCycleAtom);
  const resetFlag = useRef(false);
  const [isFace, setIsFace] = useAtom(isFaceAtom);
  const isSessionPopup = useRef(false);
  const [language, setLanguage] = useAtom(languageAtom);
  const [listeningMode] = useAtom(listeningModeAtom);
  const [memoryText, setMemoryText] = useState("");
  const [isListeningFinished, setIsListeningFinished] = useState(false);
  const [isPressed, setIsPressed] = useState(false);
  const isPressedRef = useRef(false); // touchmoveの高頻度発火に対応するための同期的なガード
  const [talkingMode] = useAtom(talkingModeAtom); // 0:通常, 1:要約のみ発話
  const [isSettingOpen] = useAtom(isSettingOpenAtom);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isTimeoutRef = useRef(false);
  const latestFiller = useRef<string>("");
  const [isFirstResponse] = useAtom(isFirstResponseAtom); // true: 有効, false: 無効
  const [isTranscribing, setIsTranscribing] = useAtom(isTranscribingAtom);
  const [, setIsBubbleVisible] = useAtom(isBubbleVisibleAtom); // true: 有効, false: 無効
  const [layoutSelect] = useAtom(layoutSelectAtom);
  const [isCallOut] = useAtom(isCallOutAtom); // true: 有効, false: 無効
  const [isNetworkError, setIsNetworkError] = useState(false);
  const [, setIsConversationInterrupted] = useAtom(isConversationInterruptedAtom); // true: 有効, false: 無効
  const isMessageEndReceivedRef = useRef(false); // message_end受信状態を追跡
  const [isFaceDetection] = useAtom(isFaceDetectionAtom); // 顔検出モード
  const isFaceOnceRef = useRef(false); // 顔検出がONになったことがあるかを追跡
  const isFaceRef = useRef(isFace); // handleCharacterCall内で最新のisFace値を参照するためのref
  isFaceRef.current = isFace; // render時に常に最新値を保持
  const warmupExecutedRef = useRef(false); // ウォームアップ実行状態を追跡
  const [, setDifyWarmupCompleted] = useAtom(difyWarmupCompletedAtom); // Difyウォームアップ完了フラグ
  const [isProgressModalReady] = useAtom(isProgressModalReadyAtom); // モーダル準備完了フラグ
  const [styleMode] = useAtom(styleModeAtom);
  const currentCharacterRef = useRef<Character | null>(null);
  const [vrmPath] = useAtom(vrmPathAtom); // ユーザーが明示的に変更した場合のみ変わるVRMパス

  const { defaultMessages } = useCustomVariables(); // デフォルトメッセージ取得
  const queueLastUpdatedRef = useRef<number>(Date.now()); // キュー最終更新時刻
  const prevVrmPathRef = useRef(vrmPath); // vrmPath変化の追跡用

  /**
   * WebSocket接続の初期化
   */
  useEffect(() => {
    const wsClient = createWSClient({
      url: getWsUrl(),
    });
    const client = createTRPCProxyClient<AppRouter>({
      links: [
        wsLink({
          client: wsClient,
        }),
      ],
    });

    // メインチャット用WebSocket
    client.socket.subscribe(
      { sessionId },
      {
        onData: (response: unknown) => {
          // 吹き出しを非表示
          setIsBubbleVisible(false);
          // タイムアウト後の応答は処理しない
          if (isTimeoutRef.current) {
            return;
          }
          // message_endを受け取ったかチェック
          if (typeof response === "string" && response.startsWith(responseTags.messageEnd)) {
            isMessageEndReceivedRef.current = true;
          }

          // タイムアウトをクリア
          clearTimeoutIfExists();

          if (typeof response === "string") {
            // TypeScriptの型安全性向上のため、unknown型を使用して型ガードを実装
            let processedResponse: string = response;

            // 失敗時の多言語対応
            if (processedResponse === "error.") {
              processedResponse = t(`failure.${styleMode}`);
            }

            // 受信したテキストを連結
            if (processedResponse.trim() !== "") {
              // キューの最終更新時刻を更新
              queueLastUpdatedRef.current = Date.now();
              setQueueArray((prevArray) => [...prevArray, processedResponse]);
            }
          }
        },
        onError: () => {
          // 吹き出しを非表示
          setIsBubbleVisible(false);
          // タイムアウト後のエラーは処理しない
          if (isTimeoutRef.current) {
            return;
          }

          // タイムアウトをクリア
          clearTimeoutIfExists();

          setQueueArray((prevArray) => [...prevArray, t(`failure.${styleMode}`)]);
        },
      }
    );

    // 要約用WebSocket
    client.summarySocket.subscribe(
      { sessionId },
      {
        onData: (response: unknown) => {
          // 吹き出しを非表示
          setIsBubbleVisible(false);
          if (typeof response === "string") {
            // TypeScriptの型安全性向上のため、unknown型を使用して型ガードを実装
            let processedResponse: string = response;

            // 失敗時の多言語対応
            if (processedResponse === "error.") {
              processedResponse = t(`failure.${styleMode}`);
            }

            // 受信したテキストを要約キューに追加
            if (processedResponse.trim() !== "") {
              queueLastUpdatedRef.current = Date.now();
              setSummaryQueueArray((prevArray) => [...prevArray, processedResponse]);
            }
          }
        },
        onError: () => {
          // 吹き出しを非表示
          setIsBubbleVisible(false);
          setSummaryQueueArray((prevArray) => [...prevArray, t(`failure.${styleMode}`)]);
        },
      }
    );

    return () => {
      // WS接続の増加を防止
      wsClient.close();

      // タイムアウトをクリア
      clearTimeoutIfExists();
    };
  }, [styleMode]);

  /**
   * 起動時の初期化
   */
  useEffect(() => {
    resetSession();
    setPlaceholderText(t(`defaultPlaceholderText.${styleMode}`));
  }, [styleMode]);

  /**
   * モーダル準備完了後にDifyウォームアップを実行
   */
  useEffect(() => {
    if (!warmupExecutedRef.current && isProgressModalReady) {
      warmupExecutedRef.current = true;
      warmupDifyConnection(sessionId)
        .then(() => {
          // ウォームアップ完了
          setDifyWarmupCompleted(true);
        })
        .catch((error) => {
          console.error("ウォームアップエラー:", error);
        });
    }
  }, [isProgressModalReady, setDifyWarmupCompleted]);

  useEffect(() => {
    setListeningPlaceholderText();
  }, [isPressed, listeningMode]);

  /**
   * 聞き取り中のプレースホルダーテキスト設定
   */
  const setListeningPlaceholderText = () => {
    if (listeningMode === 2 && !isPressed) {
      // プレースホルダーを空に設定(疑似プレースホルダで対応)
      setPlaceholderText("");
    } else {
      setPlaceholderText(t("listening"));
    }
  };

  /**
   * 言語変更時のメッセージリセット
   */
  useEffect(() => {
    // resetSession()だとスタンバイモードになるため例外処理
    const defaultMessage = defaultMessages();
    setDisplayText("");
    setMemoryText("");
    setCurrentAssistantMessage("");
    setFullAssistantMessage("");
    const message1: Message = {
      role: "assistant",
      content: defaultMessage[0],
      messageId: "0",
    };
    const message2: Message = {
      role: "assistant",
      content: defaultMessage[1],
      messageId: "0",
    };
    const message3: Message = {
      role: "assistant",
      content: defaultMessage[2],
      messageId: "0",
    };
    setMessages([message1, message2, message3]);
    setListeningPlaceholderText();
    resetFlag.current = true;
    isSessionPopup.current = false;
  }, [language, styleMode]);

  /**
   * 音声認識を開始する
   */
  const handleStartListening = () => {
    setIsListening(true);
    setIsProcessing(true);
    setIsThinking(false);
    setIsOutputText(false);
    SpeechRecognition.startListening({ language: language });
  };

  /**
   * 新しいセッションを開始する
   */
  const handleStartSession = () => {
    setIsSession(true);
    isSessionPopup.current = false;
    handleStartListening();
    setDisplayText("");
    setMemoryText("");
    setListeningPlaceholderText();
    waitTurn.current = 0;
    setStandbyMode(talkModes.active);
  };

  /**
   * テキストを音声合成して再生する
   *
   * @param inputText 音声合成するテキスト
   */
  const handlePlayVoice = async (inputText: string) => {
    const params: PlayVoiceParams = {
      inputText,
      T2SEngine,
      currentSpeaker,
      speed,
      pitch,
      intonation,
      volume,
      talkingMode,
      setIsThinking,
      setIsProcessing,
      setIsTranscribing,
      setOutputText,
      setIsInConversationCycle,
    };

    await playVoice(params);
  };

  /**
   * メイン音声合成逐次処理の開始
   * キューから取得したテキストを音声合成する
   */
  // eslint-disable-next-line complexity
  const startVoiceProcessing = async () => {
    await setIsThinking(true);
    await setIsProcessing(true);

    // 最新のqueueArrayから最初の要素を取得し、同時に削除
    let chunk: string | undefined;
    setQueueArray((prevArray) => {
      chunk = prevArray[0];
      const newArray = prevArray.slice(1);
      return newArray;
    });

    if (!chunk) {
      setIsThinking(false);
      setIsProcessing(false);
      return;
    }

    if (chunk === "") {
      setIsThinking(false);
      setIsProcessing(false);
      return;
    }

    // chunkの先頭が"<img"の場合、音声合成せずにimgを表示する
    if (chunk.startsWith(responseTags.imgTag)) {
      setOutputText(chunk); // imgタグをそのまま出力
      setIsThinking(false);
      return;
    }

    // chunkの先頭が"message_end/"の場合、その後に続く会話IDを取得する
    if (chunk.startsWith(responseTags.messageEnd)) {
      const newMessageId = chunk.split("/")[1];
      //messagesの最後の要素にmessage_idを追加
      if (messages.length > 0 && messages[messages.length - 1].messageId === "") {
        // 最後のメッセージがアシスタントの回答(id:"")の場合はmessageIdを代入
        messages[messages.length - 1].messageId = newMessageId;
      }

      // デバッグモードかどうかを判定
      const isDebugMode = newMessageId === "debug-mode";

      if (talkingMode === 0) {
        setDisplayText("");
        setMemoryText("");
        if (!isSettingOpen) {
          await handleStartListening();
        } else {
          setIsThinking(false);
          setIsProcessing(false);
        }
        PlaySound("assistant", volume);
        setListeningPlaceholderText();
        // 会話サイクル完了
        endConversationCycle();
        return;
      } else {
        // 要約モード: デバッグモードでも通常モードでも要約APIを呼び出す
        console.info("🔧 要約モード処理開始 - デバッグモード:", isDebugMode);

        // デバッグモードの場合でも要約処理を実行
        if (isDebugMode || (fullAssistantMessage && fullAssistantMessage.trim() !== "")) {
          const messageToSummary = isDebugMode
            ? "デバッグモード用メッセージ"
            : fullAssistantMessage;
          console.info("要約対象のメッセージ:", messageToSummary);

          // 要約処理を開始
          api.dify.summary
            .mutate({
              message: messageToSummary,
              userLanguage: language,
              isDebugMode: isDebugMode, // デバッグモードフラグを追加
              sessionId: sessionId, // セッションIDを追加
            })
            .then(() => {
              console.info("要約処理完了");
              setIsThinking(false);
            })
            .catch((error) => {
              console.error("要約処理エラー:", error);
              setIsThinking(false);
              endConversationCycle();
            });
        }
      }
      // setIsOutputText(false);
      return;
    }

    // responseに[表情]が含まれている場合は、抽出する
    const expressionMatch = chunk.match(/\[(.*?)\]/);
    const expression = expressionMatch ? expressionMatch[1] : null;
    // responseから[表情]を削除する
    const response = chunk.replace(/\[(.*?)\]/, "");

    // デバッグモードかどうかを判定（メッセージ内容から判定）
    const isDebugMode = messages.some((msg) => msg.messageId === "debug-mode");

    if (talkingMode === 0) {
      emitter.emit("expression", expression); // 表情を通知
      await handlePlayVoice(response);
      // テキストアニメーション用にoutputTextを設定
    } else if (talkingMode !== 0 && isTimeoutRef.current) {
      // 全文モード以外でタイムアウトした場合は音声再生と出力をどちらも行う
      emitter.emit("expression", expression);
      await handlePlayVoice(response);
      setOutputText(response);
      setIsThinking(false);
    } else {
      // 要約モードの場合
      if (isDebugMode) {
        // デバッグモード時は表示のみ（音声合成なし）
        console.info("🔧 デバッグモード: メインキューの音声合成をスキップ");
        setOutputText(response); // 表示のみ
        setIsThinking(false);
      } else {
        // 通常の要約モード
        // 現在のアシスタントメッセージに蓄積（表示用）
        setCurrentAssistantMessage((prev) => prev + response);
        // 完全なアシスタントメッセージに蓄積（要約用）
        setFullAssistantMessage((prev) => prev + response);
        // RECEPTION_API_KEYの発話は無効化（要約のみ発話するため）
        // talkingMode=1の場合はsetOutputTextのみ実行（音声合成なし、メッセージ統合はTextOutput.tsxで行う）
        setOutputText(response);
        setIsThinking(false);
      }
    }
  };

  /**
   * 1フレーズ固定メッセージの即時音声再生
   * 引数のテキストをそのまま表情のみ抽出して再生
   *
   * @param text 即時再生するテキスト
   */
  const playImmediateResponse = async (text: string) => {
    if (!text || text.trim() === "") return;
    setIsThinking(true);
    setIsProcessing(true);
    setIsTranscribing(false);
    // 表情抽出
    const expressionMatch = text.match(/\[(.*?)\]/);
    const expression = expressionMatch ? expressionMatch[1] : null;
    const response = text.replace(/\[(.*?)\]/, "");
    // 表情通知
    emitter.emit("expression", expression);
    // 音声合成
    await handlePlayVoice(response);
    setIsThinking(false);
  };

  /**
   * 要約音声合成逐次処理の開始
   * 要約キューから取得したテキストを音声合成する（表示はしない）
   */
  // eslint-disable-next-line complexity
  const startSummaryVoiceProcessing = async () => {
    if (isListening) {
      return;
    }
    if (!isThinking && summaryQueueArray.length > 0) {
      await setIsThinking(true);
      await setIsProcessing(true);

      // 最新のsummaryQueueArrayから最初の要素を取得し、同時に削除
      let chunk: string | undefined;
      setSummaryQueueArray((prevArray) => {
        chunk = prevArray[0];
        const newArray = prevArray.slice(1);
        return newArray;
      });

      if (!chunk) {
        setIsThinking(false);
        setIsProcessing(false);
        return;
      }
      if (chunk?.startsWith(responseTags.messageEnd)) {
        setIsThinking(false);
        setIsProcessing(false);
        // メッセージをリセット
        setCurrentAssistantMessage("");
        setFullAssistantMessage("");

        setDisplayText("");
        setMemoryText("");
        if (!isSettingOpen) {
          await handleStartListening();
        }

        PlaySound("assistant", volume);
        setListeningPlaceholderText();
        // 会話サイクル完了（要約モード）
        endConversationCycle();
        return;
      }

      if (!chunk) {
        setIsThinking(false);
        setIsProcessing(false);
        return;
      }

      if (chunk === "") {
        setIsThinking(false);
        setIsProcessing(false);
        return;
      }

      // chunkの先頭が"<img"の場合、音声合成しない
      if (chunk.startsWith(responseTags.imgTag)) {
        setIsProcessing(false);
        setIsThinking(false);
        return;
      }

      // responseに[表情]が含まれている場合は、抽出する
      const expressionMatch = chunk.match(/\[(.*?)\]/);
      const expression = expressionMatch ? expressionMatch[1] : null;
      // responseから[表情]を削除する
      const response = chunk.replace(/\[(.*?)\]/, "");

      emitter.emit("expression", expression); // 表情を通知

      // 要約テキストを音声合成（表示はしない）
      handlePlayVoice(response);
    }
  };

  /**
   * キューの状態監視と音声処理の開始
   */
  useEffect(() => {
    if (isListening || queueArray.length === 0 || isOutputText) return;
    if (!isThinking) {
      startVoiceProcessing();
    }
  }, [isThinking, isListening, isOutputText, queueArray]);

  /**
   * 要約キューの状態監視と要約音声処理の開始
   */
  useEffect(() => {
    if (talkingMode === 1) {
      startSummaryVoiceProcessing();
    }
  }, [isThinking, isListening, summaryQueueArray]);

  /**
   * ユーザーテキストの処理とAIへの送信
   *
   * @param text ユーザーのテキスト入力
   */
  const handleTextRecognized = async (text: string) => {
    if (!text) {
      return;
    }

    // message_end受信フラグをリセット
    isMessageEndReceivedRef.current = false;

    // 会話中断フラグを有効化
    setIsConversationInterrupted(true);

    // タイムアウトをクリア
    clearTimeoutIfExists();

    // サーバーにキャラクター名を送信する関数
    const currentCharacter = getVRMName(currentVRM);
    setIsGenerating(true);

    // 一時返答が有効、かつ除外ワードが含まれていない場合
    if (isFirstResponse && !GreetingStatus.exclusions.some((word) => text.includes(word))) {
      // 挨拶か判定し、挨拶でなければフィラーを送信する（10秒でタイムアウト）
      Promise.race([
        checkGreeting(text),
        new Promise<string>((_, reject) =>
          setTimeout(() => reject(new Error("CheckGreeting Timeout")), 10000)
        ),
      ])
        .then((isGreeting: string) => {
          if (isGreeting === GreetingStatus.no) {
            // まだ回答が追加されていない場合、かつ閾値以上時間が経過していればフィラーメッセージを送信
            const timeSinceQueueUpdate = Date.now() - queueLastUpdatedRef.current;
            if (
              shouldSendFiller(
                queueArray.length,
                summaryQueueArray.length,
                timeSinceQueueUpdate,
                FILLER_SEND_THRESHOLD_MS
              )
            ) {
              sendFillerMessage();
            }
          }
        })
        .catch(() => {
          // タイムアウト時は何もしない
        });
    }

    // タイムアウトの設定
    timeoutRef.current = setTimeout(handleTimeout, talkTimeout);

    // customerId を取得
    let customerId: string;
    try {
      customerId = await getCurrentCustomerId();
    } catch (error) {
      console.error("Customer ID authentication failed:", error);
      setIsGenerating(false);
      endConversationCycle();
      return;
    }

    api.dify.reception
      .mutate({
        customerId: customerId,
        message: text,
        resetFlag: resetFlag.current,
        characterIdentification: currentCharacter,
        userLanguage: language,
        talkingMode: talkingMode, // トーキングモードを追加
        updatePrompt: currentCharacterRef.current?.prompt, // ユーザが指定するプロンプトを追加
        sessionId: sessionId, // セッションIDを追加
        user: "user",
      })
      .then(() => {
        setIsGenerating(false);
        if (resetFlag.current) {
          resetFlag.current = false;
        }
      })
      .catch(() => {
        // タイムアウトしていない場合
        if (!isTimeoutRef.current) {
          handleTimeout();
          clearTimeoutIfExists();
        }
        // API呼び出し失敗時も会話サイクルを終了
        endConversationCycle();
      });
  };

  /**
   * 特定のワードを置き換える
   *
   * @param text 置換対象のテキスト
   * @returns 置換後のテキスト
   */
  const replaceWords = (text: string): string => {
    let replacedText = text;
    for (const [word, replacement] of Object.entries(conversionMap)) {
      replacedText = replacedText.replace(new RegExp(word, "g"), replacement);
    }
    return replacedText;
  };

  /**
   * キャラクター変更時の処理
   * vrmPathAtom はユーザーが < > ボタンやメニューで明示的に切り替えた場合のみ変わるため、
   * 内部ロード処理による意図しないリセットが発生しない
   */
  useEffect(() => {
    if (vrmPath === "") return;
    if (standbyMode !== talkModes.active) return;
    // standbyModeが変化しただけでvrmPathが変わっていない場合はリセット不要
    if (prevVrmPathRef.current === vrmPath) return;
    prevVrmPathRef.current = vrmPath;

    const reset = async () => {
      const defaultMessage = defaultMessages();
      resetFlag.current = true;
      isSessionPopup.current = false;
      await SpeechRecognition.stopListening();
      if (isSession) {
        PlaySound("cancel2", volumeMultiplier * volume);
      }
      setIsSession(false);
      setDisplayText("");
      setMemoryText("");
      setCurrentAssistantMessage("");
      setFullAssistantMessage("");

      // キャラクター情報をDBから取得
      currentCharacterRef.current = await selectByName(getVRMName(currentVRM));

      const message1: Message = {
        role: "assistant",
        content: defaultMessage[0],
        messageId: "0",
      };
      const message2: Message = {
        role: "assistant",
        content: defaultMessage[1],
        messageId: "0",
      };
      const message3: Message = {
        role: "assistant",
        content: defaultMessage[2],
        messageId: "0",
      };
      setMessages([message1, message2, message3]);
    };
    reset()
      .then(() => {
        handleStartListening();
        setIsSession(true);
        waitTurn.current = 0;
      })
      .catch((error) => {
        // reset()失敗時はセッションをリセット状態のまま維持し、音声認識は起動しない
        console.error("キャラクター変更時のリセット処理に失敗しました:", error);
        setIsSession(false);
      });
  }, [vrmPath, styleMode, standbyMode]);

  /**
   * 認識したテキストを送信し応答を待つ
   */
  const releaseText = async () => {
    if (!listening && displayText !== "") {
      // 会話サイクル開始
      setIsInConversationCycle(true);
      // 吹き出しを表示
      setIsBubbleVisible(true);
      // 表示中の文章をgptに送信
      handleTextRecognized(displayText);
      const message: Message = {
        role: "user",
        content: displayText,
        messageId: "0",
      };
      setMessages([...messages, message]);
      setDisplayText("");
      setMemoryText("");
      setIsListening(false);
      setPlaceholderText(t("thinking"));
      setIsOutputText(false);
    }
  };

  const addWaitCount = () => {
    if (isSession) waitTurn.current += 1;
    if (waitTurn.current < waitTimer || waitTimer < 0) {
      // 待機中は何もしない
    } else {
      if (messages.length > messageConstants.defaultMessageCount || isSession) {
        preResetSession();
      }
      waitTurn.current = 0;
    }
  };
  /**
   * 音声認識状態の監視と処理
   */
  // eslint-disable-next-line complexity
  useEffect(() => {
    // 録音状態が終わったときの処理
    if (!listening) {
      // テキストが空欄以外の場合
      if (displayText !== "") {
        // 音声認識完了時の処理

        // 呼びかけが有効かつ、スタンバイモードの場合はキャラクターが反応
        if (isCallOut && standbyMode === talkModes.standby) {
          waitTurn.current = 0;
          // 各キャラクターの呼びかけ処理
          // eslint-disable-next-line complexity
          const handleCharacterCall = () => {
            const currentCharacterName = getVRMName(currentVRM);

            // デフォルトの呼びかけチェック用に正規化
            const normalizedInputText = normalizeCharacterName(displayText);
            const normalizedCharName = normalizeCharacterName(currentCharacterName);

            if (
              currentCharacterName === characterNames.roboo &&
              (displayText.includes("ロボオ") || displayText.includes("ロボ"))
            ) {
              return `robooResponseList.${styleMode}`;
            } else if (
              currentCharacterName === characterNames.roboco &&
              (displayText.includes("ロボコ") || displayText.includes("ロボ"))
            ) {
              return `robocoResponseList.${styleMode}`;
            } else if (
              currentCharacterName === characterNames.reico &&
              displayText.includes("レイコ")
            ) {
              return `reicoResponseList.${styleMode}`;
            } else if (
              normalizedCharName &&
              (normalizedInputText.includes(normalizedCharName) || isFaceRef.current)
            ) {
              // キャラクター専用の定義がないモデルが呼ばれた場合はデフォルト
              return `defaultResponseList.${styleMode}`;
            }
            return "";
          };

          const responseListKey = handleCharacterCall();
          if (responseListKey) {
            PlaySound("user", volume);
            setIsThinking(false);
            setDisplayText("");
            setMemoryText("");
            const responseList = t(responseListKey, {
              returnObjects: true,
            }) as string[];

            const randomResponse = responseList[Math.floor(Math.random() * responseList.length)];
            // スタンバイモーダルを閉じる
            setStandbyMode(talkModes.active);
            // 応答メッセージを即時再生
            playImmediateResponse(randomResponse);
            initSessionFlag.current = true;
            return;
          }

          handleStartListening();
          setDisplayText("");
          setMemoryText("");
          setIsProcessing(false);
          return;
        }
        // 聞き取りモードが1,2の場合は聞き取りを継続
        if (listeningMode !== 0 && !isListeningFinished) {
          if (transcript === "") {
            addWaitCount();
          } else {
            setMemoryText(displayText);
          }
          handleStartListening();
          // setIsProcessing(false);
          return;
        }

        waitTurn.current = 0;
        PlaySound("user", volume);
        releaseText();
        setIsListeningFinished(false);
        return;
      } else {
        // テキストが空欄の場合
        if (!isListening && !isListeningFinished) return;
        if (standbyMode > 0) {
          addWaitCount();
          handleStartListening();
          return;
        }

        setIsThinking(false);
        setIsListening(false);
        setIsProcessing(false);
        setDisplayText("");
        setMemoryText("");
        PlaySound("cancel2", volumeMultiplier * volume);
      }
    }
  }, [listening, isListeningFinished, styleMode]);

  /** 会話モードに基づき表示文章を更新 **/
  useEffect(() => {
    if (listeningMode === 2 && !isPressed) return;
    setDisplayText(memoryText);
  }, [memoryText]);
  /**
   * スタンバイモード2での聞き取りループ処理
   */
  useEffect(() => {
    const loop = async () => {
      // キュー残ってたら無視
      setDisplayText("");
      setMemoryText("");
      if (queueArray.length > 0) return;
      if (summaryQueueArray.length > 0) return;

      // 状態に基づいてプレースホルダーテキストを更新
      updatePlaceholderText();
    };
    if (listeningMode === 0) {
      loop();
    }
  }, [isProcessing, isOutputText, isThinking]);

  /**
   * 現在の状態に基づいてプレースホルダーテキストを更新する
   */
  const updatePlaceholderText = () => {
    // すべての状態をチェックする前に共通の条件を定義
    const isIdle = !isProcessing && !isThinking && !isOutputText;

    // 各状態のチェックと処理を分離する
    handleActiveMode(isIdle);
    handleSessionStartCondition(isIdle);
    handleThinkingState();
  };

  /**
   * アクティブモードでの待機状態を処理する
   */
  const handleActiveMode = (isIdle: boolean) => {
    if (isIdle && standbyMode === talkModes.active) {
      setListeningPlaceholderText();
    }
  };

  /**
   * セッション開始条件を処理する
   */
  const handleSessionStartCondition = (isIdle: boolean) => {
    if (isIdle && initSessionFlag.current) {
      initSessionFlag.current = false;
      handleStartSession();
    }
  };

  /**
   * 思考中の状態を処理する
   */
  const handleThinkingState = () => {
    if (isProcessing && isThinking && standbyMode >= talkModes.standby) {
      setPlaceholderText(t("thinking"));
    }
  };

  /**
   * 文字起こしのリアルタイム表示処理
   */
  useEffect(() => {
    // 文字起こしが有効でない場合は何もしない
    if (!isTranscribing) return;

    if (transcript !== "") {
      waitTurn.current = 0;
      const replacedText = replaceWords(transcript);

      // 呼びかけが有効かつ、スタンバイモードの場合は常に表示
      if (isCallOut && standbyMode === talkModes.standby) {
        setDisplayText(replacedText);
        return;
      }

      if (listeningMode !== 0) {
        if (listeningMode === 2 && !isPressed) return;
        setDisplayText(memoryText + " " + replacedText);
      } else {
        setDisplayText(replacedText);
      }

      // ポップアップが表示されている場合は閉じる
      if (isSessionPopup.current) {
        isSessionPopup.current = false;
        triggerClosePopup();
      }
    }
  }, [transcript]);

  /**
   * 呼びかけられたときの会話応答処理
   */
  const initResponse = async () => {
    SpeechRecognition.stopListening();
    setIsListening(false);
    setDisplayText("");
    setMemoryText("");
    let responseListKey = "";

    const currentCharacterName = getVRMName(currentVRM);

    // デフォルトの呼びかけチェック用に正規化
    const normalizedInputText = normalizeCharacterName(displayText);
    const normalizedCharName = normalizeCharacterName(currentCharacterName);

    if (currentCharacterName === characterNames.roboo) {
      responseListKey = `robooResponseList.${styleMode}`;
    } else if (currentCharacterName === characterNames.roboco) {
      responseListKey = `robocoResponseList.${styleMode}`;
    } else if (currentCharacterName === characterNames.reico) {
      responseListKey = `reicoResponseList.${styleMode}`;
    } else if (
      normalizedCharName &&
      (normalizedInputText.includes(normalizedCharName) || isFaceRef.current)
    ) {
      responseListKey = `defaultResponseList.${styleMode}`;
    }

    if (responseListKey) {
      PlaySound("user", volume);
      setIsThinking(false);
      setDisplayText("");
      setMemoryText("");
      const responseList = t(responseListKey, {
        returnObjects: true,
      }) as string[];

      const randomResponse = responseList[Math.floor(Math.random() * responseList.length)];
      // スタンバイモーダルを閉じる
      setStandbyMode(talkModes.active);
      // 応答メッセージを即時再生
      playImmediateResponse(randomResponse);
      initSessionFlag.current = true;
    }
    setTimeout(() => {
      handleStartListening();
      waitTurn.current = 0;
    }, 1000);
  };

  /**
   * ネットワーク監視処理
   */
  useNetworkMonitor({
    // 切断時の処理
    onDisconnect: () => {
      // 会話モードの状態値を停止状態に設定
      setStandbyMode(talkModes.stopped);
      // waitTurnをリセット
      waitTurn.current = 0;
      // セッションポップアップ状態をリセット
      isSessionPopup.current = false;
      // 音声再生を強制終了
      audioManager.stopCurrentAudio();
      // Queueをクリア
      setQueueArray([]);
      setSummaryQueueArray([]);

      // モーダルを表示
      setIsNetworkError(true);
    },
    // 再接続時の処理
    onReconnect: () => {
      // 現時点では未使用
    },
  });

  // 会話中断イベントのリスナーをセットアップ
  useEffect(() => {
    /**
     * 会話中断時の処理
     */
    const handleConversationInterrupt = async () => {
      // customerId を取得
      let customerId: string;
      try {
        customerId = await getCurrentCustomerId();

        // message_endを受け取っていない場合のみ、Difyの生成停止APIを呼び出す
        if (!isMessageEndReceivedRef.current) {
          await api.dify.stopGeneration
            .mutate({
              sessionId: sessionId,
              customerId: customerId,
            })
            .catch((error) => {
              // 停止API呼び出しに失敗した場合も中断処理は続行
              console.error("会話中断時の停止API呼び出しに失敗:", error);
            });
        }
      } catch (error) {
        console.error("Customer ID authentication failed:", error);
        setIsGenerating(false);
        endConversationCycle();
        return;
      }

      // 音声再生を停止
      audioManager.stopCurrentAudio();

      // タイピングアニメーションを一括表示(イベント発火)
      conversationEmitter.emit("interruptTyping");

      // 状態をリセット
      setListeningPlaceholderText();
      setIsThinking(false);
      setIsProcessing(false);
      setIsListening(false);
      SpeechRecognition.stopListening();
      setMemoryText("");
      setCurrentAssistantMessage("");
      setFullAssistantMessage("");
      setIsBubbleVisible(false);
      // 会話サイクル中断
      endConversationCycle();

      // Queueをクリア
      setQueueArray([]);
      setSummaryQueueArray([]);

      // 表情をリセット
      emitter.emit("expression", "neutral");

      // 会話中断フラグを無効化
      setIsConversationInterrupted(false);

      // 中断メッセージを追加
      const interruptMessage: Message = {
        role: "assistant",
        content: t("conversationInterrupted"),
        messageId: "0",
        contentColor: "#dc3545",
      };
      setMessages((prevMessages) => [...prevMessages, interruptMessage]);

      // 少し遅延を入れてから音声認識を再開
      setTimeout(() => {
        handleStartListening();
        waitTurn.current = 0;
      }, 1000);
    };

    conversationEmitter.on("interrupt", handleConversationInterrupt);

    return () => {
      conversationEmitter.removeListener("interrupt", handleConversationInterrupt);
    };
  }, [setQueueArray, setSummaryQueueArray]);

  /**
   * スタンバイモードの変更監視
   */
  useEffect(() => {
    if (standbyMode === talkModes.active) {
      setIsSession(true);
      handleStartListening();
      setListeningPlaceholderText();
      (async () => {
        // キャラクター情報をDBから取得
        currentCharacterRef.current = await selectByName(getVRMName(currentVRM));
      })();
    } else if (standbyMode === talkModes.standby) {
      // 言語を日本語に戻す
      setLanguage(currentLocale);
      resetSession();
      i18n.changeLanguage(currentLocale);
      setPlaceholderText(t(`defaultPlaceholderText.${styleMode}`));
      // 呼びかけが有効な場合、スタンバイ中も呼びかけを検知できるように遅延処理後に音声認識を開始
      if (isCallOut) {
        setTimeout(() => {
          setIsTranscribing(true);
          handleStartListening();
          waitTurn.current = 0;
        }, 1000);
      }
    } else {
      preResetSession();
    }
  }, [standbyMode, styleMode]);

  /**
   * セッション終了予告処理
   */
  const preResetSession = async () => {
    if (standbyMode === talkModes.stopped) {
      setIsThinking(false);
      setIsListening(false);
      setIsProcessing(false);
      setIsListeningFinished(false);
      setDisplayText("");
      setMemoryText("");
      SpeechRecognition.stopListening(); // 先に↑状態変えないと別の挙動になる
      setPlaceholderText(t("stopped"));
      return;
    }
    // setDisplayText("");
    // setMemoryText("");

    if (!isOutputText && !isSessionPopup.current) {
      isSessionPopup.current = true;
      showSessionPopup(
        () => {
          // handleStartSession();
          setIsSession(true);
          isSessionPopup.current = false;
          handleStartListening();
          waitTurn.current = 0;
          setStandbyMode(talkModes.active);
          // ポップアップ後のフラグリセットを追加
          isSessionPopup.current = false;
        },
        () => {
          resetSession();
          // ポップアップ後のフラグリセットを追加
          isSessionPopup.current = false;
        },
        () => {
          resetSession();
          // ポップアップ後のフラグリセットを追加
          isSessionPopup.current = false;
        }
      );
    }
  };

  /**
   * セッションのリセット処理
   */
  const resetSession = async () => {
    const defaultMessage = defaultMessages();

    resetFlag.current = true;
    isSessionPopup.current = false;
    isFaceOnceRef.current = false;

    SpeechRecognition.stopListening();
    if (isSession) {
      PlaySound("cancel2", volumeMultiplier * volume);
    }
    setIsSession(false);
    setIsFace(false);
    setDisplayText("");
    setMemoryText("");
    setCurrentAssistantMessage("");
    setFullAssistantMessage("");
    setMessages([
      { role: "assistant", content: defaultMessage[0], messageId: "0" },
      { role: "assistant", content: defaultMessage[1], messageId: "0" },
      { role: "assistant", content: defaultMessage[2], messageId: "0" },
    ]);

    if (standbyMode !== talkModes.stopped) {
      setStandbyMode(talkModes.standby);
      setPlaceholderText(t(`defaultPlaceholderText.${styleMode}`));
    } else {
      setPlaceholderText(t("stopped"));
    }
  };

  /**
   * セッション状態の変更監視
   */
  useEffect(() => {
    if (isSession && standbyMode === talkModes.standby) {
      // セッション開始
      initResponse();
    }
  }, [isSession]);

  useEffect(() => {
    // isFaceがOFFからONに変わったことを一度だけ記録
    if (isFace && !isFaceOnceRef.current) {
      isFaceOnceRef.current = true;
    }
  }, [isFace]);

  /**
   * セッション終了告知表示条件を満たすか判定
   * isFaceDetection(顔検出)が有効
   * isFaceOnceRefがtrue(一度でも顔検出された)
   * isFaceがfalse(現在顔検出されていない)
   * isSessionがtrue(セッション中)
   * isSessionPopupがfalse(セッションポップアップが表示されていない)
   * isInConversationCycleがfalse(会話サイクル中でない)
   *
   * @returns セッション終了告知表示条件を満たす場合true、それ以外はfalse
   */
  const shouldPreResetSession = (): boolean => {
    return (
      isFaceDetection &&
      isFaceOnceRef.current &&
      !isFace &&
      isSession &&
      isSessionPopup.current === false &&
      !isInConversationCycle
    );
  };

  /**
   * 会話サイクル終了時の事後処理
   * - フィラーメッセージをキューから削除
   * - 会話サイクルフラグをリセット
   */
  const endConversationCycle = () => {
    // フィラーメッセージをキューから削除
    const fillerList = t(`fillerList.${styleMode}`, { returnObjects: true }) as string[];
    setQueueArray((prevQueue) =>
      prevQueue.filter((item) => !fillerList.some((filler) => item.includes(filler)))
    );
    setSummaryQueueArray((prevQueue) =>
      prevQueue.filter((item) => !fillerList.some((filler) => item.includes(filler)))
    );
    // 会話サイクルフラグをリセット
    setIsInConversationCycle(false);
  };

  /**
   * 顔検出状態の監視と処理
   */
  useEffect(() => {
    if (isFace && !isSession) {
      setIsSession(true);
    } else if (shouldPreResetSession()) {
      // 裏コマンド発動中の場合はリセットしない
      if (waitTimer > 1) {
        preResetSession();
      }
    }
  }, [isFace, isSession, isInConversationCycle]);

  /**
   * 言語変更時の処理
   */
  useEffect(() => {
    if (standbyMode === talkModes.active) {
      handleLanguageChange();
    } else {
      resetSession();
    }
  }, [language]);

  /**
   * 言語変更時の処理
   */
  const handleLanguageChange = () => {
    setIsListening(false);
    SpeechRecognition.stopListening();
    setDisplayText("");
    setMemoryText("");
    setCurrentAssistantMessage("");
    setFullAssistantMessage("");
    // setPlaceholderText(t("languageChanged"));

    // 少し遅延を入れてから音声認識を再開
    setTimeout(() => {
      handleStartListening();
      waitTurn.current = 0;
    }, 1000);
  };

  /**
   * メッセージ送信処理
   */
  const handleSendMessage = async () => {
    if (displayText.trim() === "") {
      return;
    }
    setIsListeningFinished(true);
    await SpeechRecognition.stopListening();
  };
  /**
   * メッセージ削除処理
   */
  const handleDeleteMessage = () => {
    setDisplayText("");
    setMemoryText("");
    PlaySound("cancel2", volumeMultiplier * volume);
    if (isListening) {
      SpeechRecognition.stopListening();
      // 少し遅延を入れてから音声認識を再開
      setTimeout(() => {
        handleStartListening();
        waitTurn.current = 0;
      }, 200);
    }
  };

  /**
   * 押下状態の開始処理
   * 音声認識を開始し、状態を更新する
   */
  const handlePressStart = () => {
    PlaySound("kako", volumeMultiplier * volume);
    waitTurn.current = 0;
    isPressedRef.current = true;
    setIsPressed(true);
    handleStartListening();
    setIsListeningFinished(false);
  };

  /**
   * 押下状態から指が離れたときの処理
   * 音声認識を停止し、状態をリセットする
   */
  const handlePressEnd = () => {
    PlaySound("koka", volumeMultiplier * volume);
    isPressedRef.current = false;
    setIsPressed(false);
    waitTurn.current = 0;
    if (isListening) {
      setIsListeningFinished(true);
      SpeechRecognition.stopListening();
    }
  };

  /**
   * ボタン外に指が移動したときの処理
   * 押下状態をキャンセルし、音声認識を停止して入力を破棄する
   */
  const handlePressLeave = async () => {
    if (isPressedRef.current) {
      PlaySound("cancel2", volumeMultiplier * volume);
    }
    waitTurn.current = 0;
    isPressedRef.current = false;
    setIsPressed(false);
    setDisplayText("");
    setMemoryText("");
    if (isListening) {
      await SpeechRecognition.stopListening();
    }
  };

  /**
   * タッチ移動イベントの処理
   * タッチがボタンの外に移動した場合、押下状態を解除し、音声認識を停止する
   *
   * @param e タッチイベント
   */
  const handleTouchMove = (e: React.TouchEvent<HTMLButtonElement>) => {
    if (!isPressedRef.current) return;
    const touch = e.touches[0];
    if (!touch) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const isOutside =
      touch.clientX < rect.left ||
      touch.clientX > rect.right ||
      touch.clientY < rect.top ||
      touch.clientY > rect.bottom;
    if (isOutside) {
      void handlePressLeave();
    }
  };

  /**
   * タイムアウト時の処理を実行する
   * 状態のリセットと音声認識の再開を行う
   */
  const handleTimeout = async () => {
    // タイムアウトフラグを有効に設定
    isTimeoutRef.current = true;

    // 文字起こしを無効に設定
    setIsTranscribing(false);

    // Difyの生成を停止する処理
    try {
      const customerId = await getCurrentCustomerId();
      // message_endを受け取っていない場合のみ、Difyの生成停止APIを呼び出す
      if (!isMessageEndReceivedRef.current) {
        await api.dify.stopGeneration
          .mutate({
            sessionId: sessionId,
            customerId: customerId,
          })
          .catch((error) => {
            // 停止API呼び出しに失敗した場合も中断処理は続行
            console.error("タイムアウト時の停止API呼び出しに失敗:", error);
          });
      }
    } catch (error) {
      console.error("Customer ID authentication failed:", error);
      setIsGenerating(false);
      return;
    }

    // タイムアウトメッセージをキューに追加
    setQueueArray(() => [t(`failure.${styleMode}`) + timeoutExpression]);

    // 状態をリセット
    setIsGenerating(false);
    setIsThinking(false);
    setIsProcessing(false);
    setIsListeningFinished(false);
    setDisplayText("");
    setMemoryText("");
    setListeningPlaceholderText();
    setIsBubbleVisible(false);
    // 会話サイクルタイムアウト
    endConversationCycle();

    // 一度停止してから音声認識を再開する
    SpeechRecognition.stopListening();
    // 遅延処理を設けないとタイムアウト時の音声再生とテキストが表示されなくなるため
    // 少し時間を空けてから音声認識を再開
    setTimeout(() => {
      handleStartListening();
      waitTurn.current = 0;
    }, 1500);
  };

  /**
   * タイムアウトをクリアするヘルパー関数
   */
  const clearTimeoutIfExists = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    isTimeoutRef.current = false;
  };

  /**
   * 挨拶判定処理
   * OpenAI APIを使用してメッセージが挨拶のみかを判定
   * @param userText 判定対象のテキスト
   * @returns 挨拶判定結果（YES/NO）
   */
  const checkGreeting = async (userText: string): Promise<string> => {
    try {
      const customerId = await getCurrentCustomerId();
      const result = await api.openai.checkGreeting.query({
        customerId,
        text: userText,
      });
      return result === GreetingStatus.yes ? GreetingStatus.yes : GreetingStatus.no;
    } catch {
      // エラーが発生した場合は、挨拶ではないとみなす
      return GreetingStatus.no;
    }
  };

  /**
   * フィラー送信処理
   */
  const sendFillerMessage = () => {
    const fillerList = t(`fillerList.${styleMode}`, {
      returnObjects: true,
    }) as string[];

    // 同じフィラーが続かないように、最後に使用したフィラーは除外
    const discontinuousFillers: string[] = fillerList.filter(
      (filler) => filler !== latestFiller.current
    );
    const filler = [discontinuousFillers[Math.floor(Math.random() * discontinuousFillers.length)]];

    // 発話モード関係なくキューに追加
    setQueueArray((prevArray) => [...prevArray, ...filler]);

    latestFiller.current = filler[0];
  };

  switch (layoutSelect) {
    case 1:
      return (
        <>
          <NetworkErrorModal show={isNetworkError} />
          <ControlButtonLayout1
            t={t}
            displayText={displayText}
            placeholderText={placeholderText}
            listeningMode={listeningMode}
            standbyMode={standbyMode}
            isPressed={isPressed}
            isListening={isListening}
            transcript={transcript}
            volume={volume}
            setDisplayText={setDisplayText}
            handleDeleteMessage={handleDeleteMessage}
            handleSendMessage={handleSendMessage}
            handlePressStart={handlePressStart}
            handlePressEnd={handlePressEnd}
            handlePressLeave={handlePressLeave}
            handleTouchMove={handleTouchMove}
          />
        </>
      );
    case 2:
      return (
        <>
          <NetworkErrorModal show={isNetworkError} />
          <ControlButtonLayout2
            t={t}
            displayText={displayText}
            placeholderText={placeholderText}
            listeningMode={listeningMode}
            standbyMode={standbyMode}
            isPressed={isPressed}
            isListening={isListening}
            transcript={transcript}
            volume={volume}
            setDisplayText={setDisplayText}
            handleDeleteMessage={handleDeleteMessage}
            handleSendMessage={handleSendMessage}
            handlePressStart={handlePressStart}
            handlePressEnd={handlePressEnd}
            handlePressLeave={handlePressLeave}
            handleTouchMove={handleTouchMove}
          />
        </>
      );
    case 3:
      return (
        <>
          <NetworkErrorModal show={isNetworkError} />
          <ControlButtonLayout3
            t={t}
            displayText={displayText}
            placeholderText={placeholderText}
            listeningMode={listeningMode}
            standbyMode={standbyMode}
            isPressed={isPressed}
            isListening={isListening}
            transcript={transcript}
            volume={volume}
            setDisplayText={setDisplayText}
            handleDeleteMessage={handleDeleteMessage}
            handleSendMessage={handleSendMessage}
            handlePressStart={handlePressStart}
            handlePressEnd={handlePressEnd}
            handlePressLeave={handlePressLeave}
            handleTouchMove={handleTouchMove}
          />
        </>
      );
    default:
      return (
        <>
          <NetworkErrorModal show={isNetworkError} />
          <div className="flex-textbox">
            <div className="chat-main-space">
              <textarea
                value={displayText}
                placeholder={placeholderText}
                readOnly={true}
                wrap={"soft"}
                className={`input-textbox ${
                  // listeningModeに応じてwidth値を変更するクラスを追加
                  listeningMode === 1
                    ? " textbox-send-button-width"
                    : listeningMode === 2
                      ? " textbox-listen-button-width"
                      : ""
                }`}
                onChange={(event) => {
                  setDisplayText(event.target.value);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                  }
                }}
              />
            </div>

            {/* ボタン押下時聞き取りモード 疑似プレースホルダ */}
            <div
              className="pseudo-placeholder-text"
              style={{
                display: getPseudoPlaceholderDisplay(
                  standbyMode,
                  listeningMode,
                  isPressed,
                  placeholderText === t("thinking")
                ),
              }}
            >
              {t("listeningMode2PlaceholderIconBefore")}
              <button className={"press-mic-btn"} disabled aria-label="microphone icon">
                <span className="material-symbols-rounded press-mic-icon">mic</span>
              </button>
              {t("listeningMode2PlaceholderIconAfter")}
            </div>

            {/*listeningModeが1のときに表示する*/}
            <div
              className="chat-button-container send-mode-container"
              style={{ display: listeningMode === 1 ? "flex" : "none" }}
            >
              <button
                data-tooltip-id="tip_erase"
                className="btn"
                onClick={handleDeleteMessage}
                disabled={displayText.trim() === "" || transcript !== "" || !isListening}
                onMouseUp={() => PlaySound("push3", volume)}
              >
                <span className="material-symbols-outlined">cancel</span>
                <Tooltip id="tip_erase" place="top" content={t("delete")} style={{ zIndex: 0 }} />
              </button>
              <button
                data-tooltip-id="tip_send"
                className="btn flex-column"
                onClick={handleSendMessage}
                disabled={displayText.trim() === "" || transcript !== "" || !isListening}
                onMouseUp={() => PlaySound("push3", volume)}
              >
                <span className="material-symbols-rounded">send</span>
                <Tooltip id="tip_send" place="bottom" content={t("send")} style={{ zIndex: 0 }} />
              </button>
            </div>
            <div
              className="chat-button-container button-mode-container"
              style={{ display: listeningMode === 2 ? "flex" : "none" }}
            >
              <button
                className={"press-mic-btn" + (isPressed ? " pressed" : "")}
                onMouseDown={handlePressStart}
                onMouseUp={handlePressEnd}
                onMouseLeave={handlePressLeave}
                onTouchStart={handlePressStart}
                onTouchEnd={handlePressEnd}
                onTouchMove={handleTouchMove}
                onTouchCancel={handlePressLeave}
                disabled={!isListening}
              >
                <span className="material-symbols-rounded press-mic-icon">mic</span>
              </button>
            </div>
          </div>
        </>
      );
  }
};

export default ControlButton;
